// @owners { team: patients-team }
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useSyncExternalStore } from 'react';
import { type NextAvailableDateMapping } from '~shared/features/next-available-date/helpers/isNextAvailableDateFresh';
import { mapNextAvailableDateResponsesToDates } from '~shared/features/next-available-date/helpers/mapNextAvailableDateResponsesToDates';
import { nextAvailableDatesQueryKeys } from '~shared/features/next-available-date/queries/nextAvailableDatesQueryKeys';
import { subscribeNextAvailableDatesQueryCache } from '~shared/features/next-available-date/queries/subscribeNextAvailableDatesQueryCache';
import { type NextAvailableDateResponse } from '~shared/types/clients';

/**
 * Subscribes to the React Query cache and returns a merged {@link NextAvailableDateMapping}
 * (`Record<string, NextAvailableDate>`) across all checkout-scoped NAD cache entries.
 *
 * The returned reference is stable — it only changes when the cache data actually changes,
 * so downstream selectors and hooks using `Object.is` comparison will not re-render unnecessarily.
 *
 * Use as a drop-in replacement for `state.nextAvailableDates.dates` (strategy **A** reads).
 */
export const useMergedNextAvailableDatesFromQueryCache = (): NextAvailableDateMapping => {
  const queryClient = useQueryClient();
  // Compare at the raw-response level so the snapshot function is idempotent: mapping functions
  // create new objects on every call, so comparing mapped output would always see "changed" and
  // trigger an infinite re-render loop via useSyncExternalStore.
  const prevResponsesRef = useRef<NextAvailableDateResponse[]>([]);
  const snapshotRef = useRef<NextAvailableDateMapping>({});

  return useSyncExternalStore(
    (onStoreChange) => subscribeNextAvailableDatesQueryCache(queryClient, onStoreChange),
    () => {
      const entries = queryClient.getQueriesData<NextAvailableDateResponse[]>({
        queryKey: nextAvailableDatesQueryKeys.checkout(),
      });
      const nextResponses = entries.flatMap(([, data]) => data ?? []);
      const prevResponses = prevResponsesRef.current;
      // getQueriesData returns entries in stable insertion order; the index-based comparison
      // is intentional and safe as long as no reordering occurs between cache events.
      const changed =
        nextResponses.length !== prevResponses.length || nextResponses.some((r, i) => r !== prevResponses[i]);
      if (changed) {
        prevResponsesRef.current = nextResponses;
        snapshotRef.current = mapNextAvailableDateResponsesToDates(nextResponses);
      }
      return snapshotRef.current; // SSR not used — server snapshot always empty
    },
    () => snapshotRef.current,
  );
};
